import { APP_ROUTES } from './routes';

/**
 * Authentication Constants
 *
 * Centralizes auth-related route paths and storage keys to avoid hardcoding
 * throughout the codebase. These constants are used by:
 * - AuthStartPage (initiates MSAL redirect)
 * - ProtectedRoute (redirects unauthenticated users)
 * - AuthCallbackPage (navigates after successful auth)
 * - AcceptTermsPage (navigates after terms acceptance)
 */

/**
 * Authentication flow type.
 * - 'signin': Standard sign-in for existing users
 * - 'signup': Direct to account creation for new users
 */
export type AuthFlow = 'signin' | 'signup';

/**
 * Auth-related route paths.
 */
export const AUTH_ROUTES = {
  /** Landing/marketing page */
  LANDING: '/',

  /** Auth start page for sign-in flow */
  SIGNIN: '/auth/signin',

  /** Auth start page for sign-up flow */
  SIGNUP: '/auth/signup',

  /** Callback page after Entra redirect */
  CALLBACK: '/auth/callback',

  /** Silent token refresh callback */
  SILENT_CALLBACK: '/auth/silent-callback',

  /** Legacy login page (kept for backward compatibility) */
  LOGIN: '/login',

  /** Legacy register page (kept for backward compatibility) */
  REGISTER: '/register',

  /** Terms acceptance page for new users */
  ACCEPT_TERMS: '/accept-terms',

  /** Default destination after successful auth */
  DASHBOARD: APP_ROUTES.overview,
} as const;

/**
 * SessionStorage key for storing the intended destination URL.
 * Used to return users to their original deep link after auth + terms acceptance.
 */
export const RETURN_TO_KEY = 'scheduledsmiles_returnTo';

/**
 * Auth-flow paths that must not be stored as returnTo (avoids redirect loops).
 */
const RETURN_TO_BLOCKED_PATHS = new Set<string>([
  AUTH_ROUTES.SIGNIN,
  AUTH_ROUTES.SIGNUP,
  AUTH_ROUTES.CALLBACK,
  AUTH_ROUTES.SILENT_CALLBACK,
  AUTH_ROUTES.ACCEPT_TERMS,
  AUTH_ROUTES.LOGIN,
  AUTH_ROUTES.REGISTER,
]);

/**
 * Validates a return-to URL is a same-origin relative path safe for client-side navigation.
 * Rejects open-redirect targets (absolute URLs, protocol-relative paths, scheme URLs).
 *
 * @param candidate - Path plus optional query and hash (e.g. `/settings?tab=reminders`)
 * @returns Sanitized path or null when the value is not safe to store
 */
export function sanitizeReturnToPath(candidate: string): string | null {
  const trimmed = candidate.trim();
  if (!trimmed.startsWith('/')) {
    return null;
  }

  if (trimmed.startsWith('//')) {
    return null;
  }

  if (trimmed.includes('\\')) {
    return null;
  }

  const pathname = trimmed.split(/[?#]/, 1)[0] ?? trimmed;
  if (RETURN_TO_BLOCKED_PATHS.has(pathname)) {
    return null;
  }

  const colonBeforeSlash = trimmed.indexOf(':');
  const firstSlash = trimmed.indexOf('/');
  if (colonBeforeSlash > 0 && (firstSlash === -1 || colonBeforeSlash < firstSlash)) {
    return null;
  }

  return trimmed;
}

/**
 * Key on React Router {@link Location} `state` when the user intentionally opens
 * the marketing home (e.g. the main app header brand link). AppContent must not
 * auto-redirect authenticated users from {@link AUTH_ROUTES.LANDING} to Overview
 * in that case. Do not set this after sign-in; it is only for explicit navigation.
 */
export const VIEW_PUBLIC_LANDING_STATE_KEY = 'viewPublicLanding' as const;

/**
 * Returns true when `state` was set for an explicit visit to the public landing page
 * (header logo, etc.), so the post-auth `/` → Overview redirect should be skipped.
 *
 * @param state - `useLocation().state` from React Router
 */
export function shouldStayOnPublicLanding(state: unknown): boolean {
  if (typeof state !== 'object' || state === null) {
    return false;
  }

  return (state as Record<string, unknown>)[VIEW_PUBLIC_LANDING_STATE_KEY] === true;
}

/**
 * Stores the return-to URL in sessionStorage.
 * Call this before redirecting to auth when a protected route is accessed.
 *
 * @param url The full path including query string (e.g., '/scheduled-items?edit=19')
 */
export const setReturnTo = (url: string): void => {
  const safe = sanitizeReturnToPath(url);
  if (!safe) {
    return;
  }

  sessionStorage.setItem(RETURN_TO_KEY, safe);
};

/**
 * Retrieves and clears the return-to URL from sessionStorage.
 * Call this after successful auth to navigate to the original destination.
 *
 * @param defaultUrl Fallback URL if no return-to is stored (defaults to overview)
 * @returns The stored return-to URL or the default
 */
export const getAndClearReturnTo = (defaultUrl: string = AUTH_ROUTES.DASHBOARD): string => {
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  const safe = returnTo ? sanitizeReturnToPath(returnTo) : null;
  if (safe) {
    return safe;
  }

  return defaultUrl;
};

/**
 * Checks if there's a pending return-to URL without consuming it.
 */
export const hasReturnTo = (): boolean => {
  return sessionStorage.getItem(RETURN_TO_KEY) !== null;
};

/**
 * Determines whether a given pathname is a public route.
 *
 * Public routes are accessible without authentication and should not trigger
 * login redirects even when an expired auth token is present.
 *
 * Business rule:
 * - `/` is the marketing landing page.
 * - `/card/*` pages are shareable card views for recipients.
 * - `/terms` and `/privacy` are legal pages accessible to everyone.
 *
 * @param pathname The URL pathname to check (e.g., window.location.pathname)
 * @returns True if the route is public and should bypass auth redirects
 */
export const isPublicPath = (pathname: string): boolean => {
  return (
    pathname === '/' ||
    pathname === '/how-it-works' ||
    pathname === '/faq' ||
    pathname === '/guides' ||
    pathname.startsWith('/guides/') ||
    pathname === '/ai' ||
    pathname.startsWith('/ai/') ||
    pathname === '/cards' ||
    pathname.startsWith('/cards/') ||
    pathname === '/flowers' ||
    pathname.startsWith('/flowers/') ||
    pathname === '/mailed-cards' ||
    pathname.startsWith('/mailed-cards/') ||
    pathname === '/gifts' ||
    pathname.startsWith('/gifts/') ||
    pathname === '/pricing' ||
    pathname === '/autopilot' ||
    pathname === '/never-miss' ||
    pathname === '/terms' ||
    pathname === '/privacy' ||
    pathname.startsWith('/card/') ||
    pathname === '/agent/ecard-review' ||
    pathname === '/agent/contact-change-review' ||
    pathname === '/agent/order-review' ||
    pathname.startsWith('/reminders/') ||
    pathname === '/account/marketing-unsubscribe' ||
    pathname === '/help/email-delivery' ||
    (import.meta.env.MODE === 'e2e' && pathname === '/__e2e/phone')
  );
};
